import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';

import alert from '@drivesally/sally-ui/lib/libs/alert';
import parseError from '@drivesally/sally-fw/lib/error/parseError';
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';
import listDriversApi from '@drivesally/sally-fn/lib/driver/api/list.api.driver';
import listRentalsApi from '@drivesally/sally-fn/lib/rental/api/list.api.rental';
import {isIn} from '@drivesally/sally-fn/lib/libs/apiQueryFilterExpressions';
import activeState from '@drivesally/sally-fn/lib/rental/state/active.state.rental';
import reservedState from '@drivesally/sally-fn/lib/rental/state/reserved.state.rental';

import {v4} from 'uuid';

import createTicketApi from '../../api/createTicket.api.crm';
import listTagsApi from '../../api/listTags.api.crm';
import listTeamsApi from '../../api/listTeams.api.crm';
import listTicketsApi from '../../api/listTickets.api.crm';

import CrmCreateTicketModal, {
  STEP_BLOCKED,
  STEP_DETAILS,
  STEP_DRIVER,
} from '../../components/CrmCreateTicketModal/CrmCreateTicketModal';

import {routeTicket} from '../../pages/CrmPage/route';
import showCreateTicketModalEvent from '../../events/showCreateTicketModal.event.crm';
import showTicketDetailsEvent from '../../events/showTicketDetails.event.crm';

class CrmCreateTicketContainer extends Component {
  static propTypes = {
    subscribe: PropTypes.func,
    history: PropTypes.object,
  };

  static OPTIONS = {
    perPage: 25,
  };

  state = {
    assignedTeamId: '',
    checkingOpenTickets: false,
    creating: false,
    driverLocked: false,
    drivers: [],
    driversLoading: false,
    driversPage: 1,
    driversPages: 1,
    openTickets: [],
    search: '',
    selectedDriver: null,
    selectedTagIds: [],
    step: STEP_DRIVER,
    summary: '',
    tags: [],
    teams: [],
    visible: false,
  };

  componentDidMount() {
    this.mounted = true;
    this.props.subscribe(showCreateTicketModalEvent.subscribe(this.show));
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  show = (payload) => {
    const driver = payload?.driver;
    this.setState(
      {
        assignedTeamId: '',
        checkingOpenTickets: false,
        creating: false,
        driverLocked: !!driver,
        drivers: [],
        driversPage: 1,
        driversPages: 1,
        openTickets: [],
        search: '',
        selectedDriver: null,
        selectedTagIds: [],
        step: STEP_DRIVER,
        summary: '',
        visible: true,
      },
      () => {
        this.loadTeamsAndTags();
        if (driver) {
          this.onSelectDriver(driver)();
        } else {
          this.loadDrivers({page: 1, search: ''});
        }
      }
    );
  };

  hide = () => {
    const {checkingOpenTickets, creating} = this.state;
    if (checkingOpenTickets || creating) return;
    this.setState({visible: false});
  };

  loadTeamsAndTags = async () => {
    try {
      const [teamsResponse, tagsResponse] = await Promise.all([
        listTeamsApi({limit: 100, ordering: 'name'}),
        listTagsApi({limit: 1000, ordering: 'tag'}),
      ]);
      if (!this.mounted) return;
      this.setState({
        teams: teamsResponse.results || [],
        tags: tagsResponse.results || [],
      });
    } catch (error) {
      if (!this.mounted) return;
      const {message} = parseError(error);
      alert.error(message || 'Failed to load teams and tags');
      this.setState({teams: [], tags: []});
    }
  };

  loadDrivers = async ({
    page = this.state.driversPage,
    search = this.state.search,
    drivers = this.state.drivers,
  } = {}) => {
    const apiId = v4();
    this.driversApiId = apiId;

    const reset = page === 1;
    const {perPage} = this.constructor.OPTIONS;

    this.setState({driversLoading: true, driversPage: page, search});

    const query = {
      limit: perPage,
      offset: (page - 1) * perPage,
      ordering: '-created_at',
    };
    if (search.trim()) query.search = search.trim();

    try {
      const {results, count} = await listDriversApi(query);
      if (!this.mounted || this.driversApiId !== apiId) return;

      const svidByDriverId = await this.loadDriverRentalSvids(
        results.map((driver) => driver.id)
      );
      if (!this.mounted || this.driversApiId !== apiId) return;

      const enrichedResults = results.map((driver) => ({
        ...driver,
        activeRentalSvid: svidByDriverId[driver.id] || null,
      }));

      this.setState({
        drivers: reset ? [...enrichedResults] : [...drivers, ...enrichedResults],
        driversLoading: false,
        driversPages: Math.ceil(count / perPage) || 1,
      });
    } catch (error) {
      if (!this.mounted || this.driversApiId !== apiId) return;
      this.setState({
        drivers: [],
        driversLoading: false,
        driversPages: 1,
      });
    }
  };

  loadDriverRentalSvids = async (driverIds) => {
    if (!driverIds.length) return {};

    try {
      const {results} = await listRentalsApi({
        [isIn('rentaldriver__driver')]: driverIds.join(','),
        [isIn('state')]: [activeState.key, reservedState.key].join(','),
      });

      const svidByDriverId = {};
      (results || []).forEach((rental) => {
        const svid = rental.vehicle?.svid;
        if (!svid) return;
        (rental.drivers || []).forEach(({driver}) => {
          if (driver?.id != null) svidByDriverId[driver.id] = svid;
        });
      });
      return svidByDriverId;
    } catch (error) {
      return {};
    }
  };

  onDriverSearch = (search) => {
    this.loadDrivers({page: 1, search, drivers: []});
  };

  onDriverMore = () => {
    const {driversPage, driversPages, driversLoading} = this.state;
    if (driversLoading || driversPage >= driversPages) return;
    this.loadDrivers({page: driversPage + 1});
  };

  onSelectDriver = (driver) => async () => {
    if (this.state.checkingOpenTickets) return;

    const apiId = v4();
    this.openTicketsApiId = apiId;
    this.setState({
      checkingOpenTickets: true,
      selectedDriver: driver,
      openTickets: [],
    });

    try {
      const {results} = await listTicketsApi({
        driver: driver.id,
        state: 'open',
        limit: 25,
      });
      if (!this.mounted || this.openTicketsApiId !== apiId) return;

      const openTickets = results || [];
      if (openTickets.length) {
        this.setState({
          checkingOpenTickets: false,
          openTickets,
          step: STEP_BLOCKED,
        });
        return;
      }

      this.setState({
        assignedTeamId: '',
        checkingOpenTickets: false,
        openTickets: [],
        selectedTagIds: [],
        step: STEP_DETAILS,
        summary: '',
      });
    } catch (error) {
      if (!this.mounted || this.openTicketsApiId !== apiId) return;
      const {message} = parseError(error);
      alert.error(message || 'Failed to check open tickets for this driver');
      this.setState({
        checkingOpenTickets: false,
        selectedDriver: null,
      });
    }
  };

  onChangeDriver = () => {
    if (this.state.creating) return;
    this.setState({
      assignedTeamId: '',
      openTickets: [],
      selectedDriver: null,
      selectedTagIds: [],
      step: STEP_DRIVER,
      summary: '',
    });
  };

  onOpenExistingTicket = (ticket) => () => {
    this.setState({visible: false});
    showTicketDetailsEvent.publish(ticket);
  };

  onTeamChange = (assignedTeamId) => {
    this.setState({assignedTeamId});
  };

  onSummaryChange = (summary) => {
    this.setState({summary});
  };

  onTagToggle = (tagId) => () => {
    this.setState(({selectedTagIds}) => ({
      selectedTagIds: selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id) => id !== tagId)
        : [...selectedTagIds, tagId],
    }));
  };

  onCreate = async () => {
    const {
      assignedTeamId,
      creating,
      selectedDriver,
      selectedTagIds,
      summary,
    } = this.state;
    if (creating || !selectedDriver?.id || !assignedTeamId) return;

    this.setState({creating: true});
    try {
      const payload = {
        driver: selectedDriver.id,
        assigned_team: Number(assignedTeamId),
        tags: selectedTagIds,
      };
      const trimmedSummary = summary.trim();
      if (trimmedSummary) payload.summary = trimmedSummary;

      const ticket = await createTicketApi(payload);
      if (!this.mounted) return;

      this.setState({creating: false, visible: false});
      showTicketDetailsEvent.publish(ticket);
      this.props.history.push(routeTicket(ticket.id));
    } catch (error) {
      if (!this.mounted) return;
      const {message} = parseError(error);
      alert.error(message || 'Failed to create support ticket');
      this.setState({creating: false});
    }
  };

  render() {
    const {
      assignedTeamId,
      checkingOpenTickets,
      creating,
      driverLocked,
      drivers,
      driversLoading,
      driversPage,
      driversPages,
      openTickets,
      search,
      selectedDriver,
      selectedTagIds,
      step,
      summary,
      tags,
      teams,
      visible,
    } = this.state;

    return (
      <CrmCreateTicketModal
        assignedTeamId={assignedTeamId}
        checkingOpenTickets={checkingOpenTickets}
        creating={creating}
        driverLocked={driverLocked}
        drivers={drivers}
        driversLoading={driversLoading}
        driversMore={!driversLoading && driversPage < driversPages}
        onChangeDriver={this.onChangeDriver}
        onClose={this.hide}
        onCreate={this.onCreate}
        onDriverMore={this.onDriverMore}
        onDriverSearch={this.onDriverSearch}
        onOpenExistingTicket={this.onOpenExistingTicket}
        onSelectDriver={this.onSelectDriver}
        onSummaryChange={this.onSummaryChange}
        onTagToggle={this.onTagToggle}
        onTeamChange={this.onTeamChange}
        openTickets={openTickets}
        search={search}
        selectedDriver={selectedDriver}
        selectedTagIds={selectedTagIds}
        step={step}
        summary={summary}
        tags={tags}
        teams={teams}
        visible={visible}
      />
    );
  }
}

export default subscriptionHOC(withRouter(CrmCreateTicketContainer));
